/**
 * AtAGlanceRail — CCD-DASH-014 Phase A · R4.
 *
 * Four compact link cards → Budget · Reservations · Travelers · Route.
 * Each answers "should I click into that workspace?" — never a full
 * data surface. Every card is a workspace launcher, not a summary.
 *
 * Design: /app/docs/design/CCD-DASH-014-dashboard-design-sprint.md §1.1 · R4
 */
import React from "react";
import { useCruise } from "@/context/CruiseContext";
import { Wallet, Utensils, Users, Route as RouteIcon, ArrowUpRight } from "lucide-react";

function GlanceCard({ icon, label, primary, secondary, onClick, testId, accent = "#071e3e" }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group text-left rounded-xl border border-slate-200 bg-white p-4 hover:border-slate-400 hover:shadow-md transition-all"
      data-testid={testId}
    >
      <div className="flex items-center justify-between">
        <div
          className="flex items-center gap-1.5 font-body text-[10px] font-semibold uppercase tracking-[0.14em]"
          style={{ color: accent }}
        >
          {icon}
          <span>{label}</span>
        </div>
        <ArrowUpRight className="w-3.5 h-3.5 text-slate-400 group-hover:text-slate-700 transition-colors" />
      </div>
      <div className="mt-3 font-display text-lg font-semibold text-slate-900 leading-tight">
        {primary}
      </div>
      {secondary && (
        <div className="mt-0.5 font-body text-[12px] text-slate-500 leading-snug">
          {secondary}
        </div>
      )}
    </button>
  );
}

function _countPartyMembers(voyage) {
  const party = voyage?.party_members || [];
  return party.length;
}

function _fmtMoney(amount) {
  if (amount === null || amount === undefined) return null;
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(amount);
  } catch { return `$${amount}`; }
}

export function AtAGlanceRail({ voyage, readiness }) {
  const { setRoute, portCalls } = useCruise();
  const finance = voyage?.finance || {};
  const budgetAmount = finance.total_budget_usd;
  const partyCount = _countPartyMembers(voyage);
  const portCount = (portCalls || []).filter((pc) => pc?.kind === "port").length;

  // Reservations = dining + excursions + add-ons (derived from readiness domain earned counts).
  const diningDomain = readiness?.domains?.find((d) => d.id === "dining");
  const excursionsDomain = readiness?.domains?.find((d) => d.id === "excursions");
  const diningEarned = diningDomain?.earned || 0;
  const excursionsEarned = excursionsDomain?.earned || 0;

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="at-a-glance-rail">
      <GlanceCard
        icon={<Wallet className="w-3 h-3" />}
        label="Budget"
        primary={_fmtMoney(budgetAmount) || "Not set"}
        secondary={budgetAmount ? "Trip budget on file" : "Set your travel budget"}
        onClick={() => setRoute("finances")}
        testId="glance-budget"
        accent="#4f46e5"
      />
      <GlanceCard
        icon={<Utensils className="w-3 h-3" />}
        label="Reservations"
        primary={diningEarned + excursionsEarned > 0 ? "Some booked" : "None yet"}
        secondary="Dining · excursions · add-ons"
        onClick={() => setRoute("dining")}
        testId="glance-reservations"
        accent="#e11d48"
      />
      <GlanceCard
        icon={<Users className="w-3 h-3" />}
        label="Travelers"
        primary={partyCount > 0 ? `${partyCount} on the voyage` : "Add travelers"}
        secondary="Party · passports · roles"
        onClick={() => setRoute("travelers")}
        testId="glance-travelers"
        accent="#7c3aed"
      />
      <GlanceCard
        icon={<RouteIcon className="w-3 h-3" />}
        label="Route"
        primary={portCount > 0 ? `${portCount} port${portCount === 1 ? "" : "s"}` : "See the map"}
        secondary="Itinerary preview"
        onClick={() => setRoute("cruise-map")}
        testId="glance-route"
        accent="#0891b2"
      />
    </div>
  );
}

export default AtAGlanceRail;
